import { useNavigate } from "@solidjs/router";
import { useUserContext } from "../components/UserContextProvider";
import { useSFetch } from "./sfetch";
import { setFeatureFlags, clearFeatureFlags } from "../services/featureFlags";
import { setViewingAsMember } from "../services/memberView";

const ONBOARDING_SKIP = [
    '/onboarding', '/verifyaccount', '/login', '/register', '/me', '/account-switcher',
    '/', '/downloads', '/goggles', '/vektorutils', '/tutorial', '/jobs', '/security', '/sicherheit',
    '/impressum', '/imprint', '/datenschutz', '/privacy', '/maintenance',
    '/oauth', '/activatesession', '/activationdone', '/xdsm', '/dsmdemo',
];

// Gated on an already logged-in user, so a fresh load against a broken backend cannot loop.
function reloadWhenOnline() {
    if (navigator.onLine) {
        window.location.reload();
        return;
    }
    window.addEventListener('online', () => window.location.reload(), { once: true });
}

export function useLoadCurrentUser(options?: { onboarding?: boolean }) {
    const navigate = useNavigate();
    const sfetch = useSFetch();
    const { user, setUser, setUserTenant, setSessionChecked } = useUserContext();
    const withOnboarding = options?.onboarding ?? true;

    return async function loadCurrentUser() {
        try {
            const response = await sfetch("/api/User");
            const data = response;
            if (data.ok) {
                const userdata = data.value;
                setUser({ ...user(),
                    username: userdata.username,
                    id: userdata.id,
                    uuid: userdata.minecraftUUID,
                    minecraftUsernameVerified: userdata.minecraftUsernameVerified,
                    permissions: userdata.permissions,
                    loggedin: true,
                    showAdminUiInMemberApp: (data as any).showAdminUiInMemberApp === true,
                });
                setFeatureFlags((data as any).featureFlags ?? null);
                setViewingAsMember((data as any).viewingAsMember === true);
                if (withOnboarding && !userdata.minecraftUsernameVerified && window.location.pathname !== "/verifyaccount") {
                    navigate("/verifyaccount");
                }

                const tenantResponse = await sfetch("/api/User/tenant");
                if (tenantResponse.ok) {
                    setUserTenant(tenantResponse.value ?? null);
                }

                if (withOnboarding) {
                    const skipOnboarding = ONBOARDING_SKIP.some(p => window.location.pathname === p || window.location.pathname.startsWith(p + '/'));
                    if (!skipOnboarding && userdata.minecraftUsernameVerified && (userdata.permissions ?? 0) === 0) {
                        navigate('/onboarding');
                    }
                }
            } else if (data.status === 401) {
                // Only a 401 ends the session; a timeout or 5xx after sleep or mid-deploy must not.
                setUser({ ...user(), loggedin: false });
                clearFeatureFlags();
                // Otherwise the next account on this browser inherits the banner.
                setViewingAsMember(false);
            } else if (user().loggedin) {
                reloadWhenOnline();
            }
        } catch (_error) {
            if (user().loggedin) reloadWhenOnline();
        } finally {
            setSessionChecked(true);
        }
    };
}
